import {
  Dispatch, SetStateAction, useEffect, useState,
} from 'react';
import { useTranslation } from 'next-i18next';
import {
  Button, Label,
  RecordInterface, GlobalValuesTypes, SetFormValues,
} from '@components/common/Forms';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import {
  useJobRequirementQuestions,
} from '@components/templates/job-add/edit/hooks/useJobRequirementQuestions';
import { JobRequirementQuestion } from '@components/templates/job-add/edit/form/partials/JobRequirementQuestion';
import { MirrorData, SetMirrorData } from '@type/job-add';
import { MAX_ENABLED_INTERVIEW_QUESTIONS } from 'src/constants';

/** Props */
interface JobRequirementQuestionsProps {
  record: RecordInterface,
  getFieldValue: (name: string) => GlobalValuesTypes,
  setFormValues: SetFormValues,
  updateFormElements?: () => void;
  setRequirementQuestionList?: Dispatch<SetStateAction<ApiSchema<'InterviewQuestion'>[] | undefined>>;
  initialRequirementQuestions?: ApiSchema<'InterviewQuestion'>[];
  reqMirrorData?: Pick<MirrorData, 'title' | 'description'>,
  setMirrorData?: SetMirrorData,
  locale: Locale,
  shouldFetchQuestions?:boolean,
}

/**
 * @description Job Requirement Questions list component.
 * - `record` - record interface from useBjForm
 * - `getFieldValue` - get fields value function from useBjForm
 * - `updateFormElements` - update form elements function from useBjForm
 * - `initialRequirementQuestions` - initial requirement questions;
 * - `setRequirementQuestionList` - set requirement question list function
 */
export const JobRequirementQuestions = (props: JobRequirementQuestionsProps) => {
  // Destructure props
  const {
    record,
    getFieldValue,
    updateFormElements,
    initialRequirementQuestions,
    setRequirementQuestionList,
    shouldFetchQuestions,
    reqMirrorData,
    setMirrorData,
    locale,
    setFormValues,
  } = props;

  // Translation
  const { t } = useTranslation('job-add');
  const inputName = 'requirementQuestions';

  // Requirement questions hook
  const {
    requirementRecordOptions,
    requirementQuestions,
    setRequirementQuestions,
    addQuestion,
    deleteQuestion,
    saveQuestions,
  } = useJobRequirementQuestions({
    locale,
    setFormValues,
    inputName,
    getFieldValue,
    updateFormElements,
    // have at least an empty field, just like all the rest of the form's elements
    initialRequirementQuestions: initialRequirementQuestions && initialRequirementQuestions?.length > 0
      ? initialRequirementQuestions
      : [{ question: '', enabled: true, readOnly: false }],
    setRequirementQuestionList,
    shouldFetchQuestions,
    reqMirrorData,
    setMirrorData,
  });

  // State for enabled count + message
  const [enabledCount, setEnabledCount] = useState(0);
  const [limitMessage, setLimitMessage] = useState('');

  // Initialize enabled count based on current requirementQuestions
  useEffect(() => {
    const count = requirementQuestions?.filter((q) => q?.enabled)?.length ?? 0;
    setEnabledCount(count);
    if (count < MAX_ENABLED_INTERVIEW_QUESTIONS) {
      setLimitMessage('');
    }
  }, [requirementQuestions]);

  // Render
  // **************************
  return (
    <Label
      as="div"
      className="!mt-0"
      description={t('job.add.label.requirement_questions')}
      descriptionClass="font-bold text-ink-medium px-3.5"
    >
      {/* Description tip */}
      <span className="block px-3.5 text-sm leading-snug text-ink-light">
        {t('job.add.label.requirement_questions_help')}
      </span>

      <div className="mt-3 grid grid-cols-1 justify-items-start gap-4">
        {/* Questions */}
        {requirementRecordOptions.map((question, index) => (
          <JobRequirementQuestion
            key={`${question.id}-${question.defaultValue || index}`}
            record={record}
            recordOptions={question}
            saveQuestions={saveQuestions}
            deleteQuestion={() => deleteQuestion(index)}
            inputName={inputName}
            showDeleteButton={requirementRecordOptions.length > 1}
            currentIndex={index}
            enabled={requirementQuestions?.[index]?.enabled ?? true}
            readOnly={requirementQuestions?.[index]?.readOnly ?? false}
            onToggleEnabled={(checked) => {
              setRequirementQuestions((prev) => {
                const list = [...(prev ?? [])];
                if (!list[index]) return list;

                const currentCount = list.filter((q) => q?.enabled).length;

                if (checked && currentCount >= MAX_ENABLED_INTERVIEW_QUESTIONS) {
                  setLimitMessage(t('global-label-maximum-3-question'));
                  return list;
                }

                // If below limit, clear any old message
                setLimitMessage('');

                list[index] = { ...list[index], enabled: checked } as ApiSchema<'InterviewQuestion'>;
                return list;
              });
            }}

          />
        ))}

        {limitMessage && (
          <p className="mt-2 text-sm text-red-500">{limitMessage}</p>
        )}

        {/* Add question button. Max 3 questions */}
        {(enabledCount < MAX_ENABLED_INTERVIEW_QUESTIONS) && (
          <Button
            color="light"
            styling="soft"
            size="sm"
            rounding="full"
            className="gap-1 !py-1.5 !pl-3 !pr-4 !font-normal leading-tight"
            onClick={addQuestion}
          >
            <PlusCircleIcon className="size-5" />
            {t('job.add.collection_add.questions')}
          </Button>
        )}
      </div>
    </Label>
  );
};
